import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Evenement } from '../models/evenement.model';
import { EvenementService } from '../services/evenement.service';
import { LangService, Langue } from '../services/lang.service';

@Component({
  selector: 'app-programme',
  templateUrl: './programme.component.html',
  styleUrls: ['./programme.component.css']
})
export class ProgrammeComponent implements OnInit {

  evenements: Evenement[] = [];
  chargementEnCours = true;
  messageErreur = '';
  langueActuelle: Langue = 'fr';

  // Evenement actuellement affiche dans la fenetre de details (null = fermee)
  evenementSelectionne: Evenement | null = null;

  // ---------- Calendrier reel + filtres ----------
  dateSelectionnee: string | null = null;   // format 'YYYY-MM-DD', null = toutes les dates
  categorieSelectionnee = '';               // '' = toutes les categories
  moisAffiche = new Date(2026, 6, 1);       // juillet 2026 par defaut : mois du festival

  constructor(
    private evenementService: EvenementService,
    private langService: LangService,
    private sanitizer: DomSanitizer
  ) {
    this.langService.langue$.subscribe(l => this.langueActuelle = l);
  }

  ngOnInit(): void {
    this.evenementService.getEvenements().subscribe({
      next: (data) => {
        this.evenements = data;
        this.chargementEnCours = false;
      },
      error: (err) => {
        console.error('Erreur lors du chargement des evenements', err);
        this.messageErreur = 'Impossible de charger le programme pour le moment. Vérifiez que le backend est demarré.';
        this.chargementEnCours = false;
      }
    });
  }

  // ---------- Categories disponibles (pour le filtre) ----------
  get categories(): string[] {
    const ensemble = new Set(this.evenements.map(e => e.categorie).filter(Boolean));
    return Array.from(ensemble);
  }

  // ---------- Liste finale affichee, une fois date + categorie appliquees ----------
  get evenementsFiltres(): Evenement[] {
    return this.evenements.filter(e =>
      (!this.dateSelectionnee || e.dateEvent === this.dateSelectionnee) &&
      (!this.categorieSelectionnee || e.categorie === this.categorieSelectionnee)
    );
  }

  choisirDate(date: string | null): void {
    this.dateSelectionnee = this.dateSelectionnee === date ? null : date;
  }

  effacerFiltres(): void {
    this.dateSelectionnee = null;
    this.categorieSelectionnee = '';
  }

  // ---------- Grille de calendrier du mois affiche ----------
  get nomMois(): string {
    const locales: Record<Langue, string> = { fr: 'fr-FR', en: 'en-US', ar: 'ar-TN' };
    return this.moisAffiche.toLocaleDateString(locales[this.langueActuelle], { month: 'long', year: 'numeric' });
  }

  get lettresJours(): string[] {
    const table: Record<Langue, string[]> = {
      fr: ['L', 'M', 'M', 'J', 'V', 'S', 'D'],
      en: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
      ar: ['ن', 'ث', 'ر', 'خ', 'ج', 'س', 'ح']
    };
    return table[this.langueActuelle];
  }
  get joursDuCalendrier(): { date: string | null; jour: number | null; aDesEvenements: boolean }[] {
    const annee = this.moisAffiche.getFullYear();
    const mois = this.moisAffiche.getMonth();
    const premierJourSemaine = (new Date(annee, mois, 1).getDay() + 6) % 7; // lundi = 0
    const nbJours = new Date(annee, mois + 1, 0).getDate();

    const datesAvecEvenements = new Set(this.evenements.map(e => e.dateEvent));
    const cases: { date: string | null; jour: number | null; aDesEvenements: boolean }[] = [];

    for (let i = 0; i < premierJourSemaine; i++) {
      cases.push({ date: null, jour: null, aDesEvenements: false });
    }
    for (let jour = 1; jour <= nbJours; jour++) {
      const date = `${annee}-${String(mois + 1).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;
      cases.push({ date, jour, aDesEvenements: datesAvecEvenements.has(date) });
    }
    return cases;
  }

  moisPrecedent(): void {
    this.moisAffiche = new Date(this.moisAffiche.getFullYear(), this.moisAffiche.getMonth() - 1, 1);
  }

  moisSuivant(): void {
    this.moisAffiche = new Date(this.moisAffiche.getFullYear(), this.moisAffiche.getMonth() + 1, 1);
  }

  titreLangue(ev: Evenement): string {
    if (this.langueActuelle === 'en') return ev.titreEn || ev.titre;
    if (this.langueActuelle === 'ar') return ev.titreAr || ev.titre;
    return ev.titre;
  }

  descriptionLangue(ev: Evenement): string {
    if (this.langueActuelle === 'en') return ev.descriptionEn || ev.description;
    if (this.langueActuelle === 'ar') return ev.descriptionAr || ev.description;
    return ev.description;
  }

  ouvrirDetails(evenement: Evenement): void {
    this.evenementSelectionne = evenement;
  }

  fermerDetails(): void {
    this.evenementSelectionne = null;
  }

  // Angular bloque par securite les URL "brutes" dans un iframe :
  // on doit explicitement dire qu'on lui fait confiance avec DomSanitizer.
  urlVideoSecurisee(youtubeId: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${youtubeId}`);
  }

  urlCarteSecurisee(lieu: string): SafeResourceUrl {
    const adresse = encodeURIComponent((lieu || 'Théâtre de Sidi Dhaher') + ', Sousse, Tunisie');
    return this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.google.com/maps?q=${adresse}&output=embed`);
  }

  estVideo(url: string | undefined | null): boolean {
    if (!url) return false;
    return /\.(mp4|webm|mov|avi)(\?.*)?$/i.test(url);
  }
}
